import {
  Children,
  PropsWithChildren,
  ReactNode,
  cloneElement,
  isValidElement,
} from 'react';
import { ICardProps } from '@/common/components/Card/Card';
import { Section } from '@/common/components/Section/Section';
import { Stack } from '@/common/components/Stack/Stack';
import { tv } from '@/common/theme/tv.config';
import { TGap } from '@/common/theme/variants';
import { removeFalsey } from '@coverflex-tech/hypernova-utils';

export function CardGroup({
  title,
  description,
  gap,
  size = 'md',
  plain,
  children,
  className,
}: TCardGroupProps) {
  const validChildren = Children.toArray(children).filter(removeFalsey);
  const numOfCards = validChildren.length;

  return (
    <Section title={title} description={description}>
      <Stack gap={gap} className={styles.group({ plain, className })}>
        {validChildren.map((child, idx) => {
          if (isValidElement<ICardProps>(child)) {
            return cloneElement<ICardProps>(child, {
              size,
              border: false,
              plain: true,
              className: styles.child({
                roundedTop: idx === 0,
                roundedBottom: idx === numOfCards - 1,
                className: child.props.className,
              }),
            });
          }
          return child;
        })}
      </Stack>
    </Section>
  );
}

const styles = {
  group: tv({
    base: 'rounded-xl shadow-resting-neutral10',
    variants: {
      plain: { true: 'shadow-none' },
    },
  }),
  child: tv({
    base: '',
    variants: {
      roundedTop: { true: 'rounded-t-xl' },
      roundedBottom: { true: 'rounded-b-xl' },
      errorTop: { true: 'border border-b-0 border-systemError' },
      errorBottom: { true: 'border border-t-0 border-systemError' },
    },
  }),
};

export type TCardGroupProps = {
  title?: string;
  description?: ReactNode;
  gap?: TGap;
  size?: 'sm' | 'md';
  plain?: boolean;
  className?: string;
} & PropsWithChildren;
